// File: src/js/components/scrollToTop.js
// Por qué: módulo autocontenido, sin dependencias, KISS/DRY.

/**
 * Inicializa el control scroll-to-top.
 * @param {Object} [options]
 * @param {string|Element} [options.selector='[data-scrolltop]'] Selector o elemento del control
 * @param {number} [options.threshold=300] Distancia en px para mostrar el control (sobrescribible con data-threshold)
 * @returns {Function} destroy() para desuscribir listeners
 */
export function initScrollToTop(options = {}) {
  const cfg = { selector: "[data-scrolltop]", threshold: 300, ...options };
  const el =
    typeof cfg.selector === "string"
      ? document.querySelector(cfg.selector)
      : cfg.selector;
  if (!el) return () => {};
  if (el.__scrollTopInit) return el.__scrollTopInit; // evita doble init

  const threshold =
    Number(el.getAttribute("data-threshold") || cfg.threshold) || 300;
  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)");

  let ticking = false;
  const getScrollY = () =>
    window.scrollY || document.documentElement.scrollTop || 0;
  const shouldShow = () => getScrollY() > threshold;
  const updateVisibility = () => el.toggleAttribute("hidden", !shouldShow());

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      updateVisibility();
      ticking = false;
    });
  }

  function onClick(ev) {
    ev.preventDefault();
    const reduced = prefersReduced.matches;
    try {
      window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
    } catch {
      window.scrollTo(0, 0);
    }
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });
  el.addEventListener("click", onClick);

  updateVisibility();

  const destroy = () => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onScroll);
    el.removeEventListener("click", onClick);
  };

  // marca de inicialización para no duplicar
  el.__scrollTopInit = destroy;
  return destroy;
}

export default initScrollToTop;
